'use client';

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { useEsEscritorio } from './useEsEscritorio';

interface EntradaIndice {
    id: string;
    texto: string;
}

interface PropiedadesIndicePagina {
    entradas: EntradaIndice[];
    /** Texto visible: «En esta página» (funcionalidad) o «En este artículo» (blog). */
    etiqueta: string;
    /** Nombre del `<nav>` de escritorio: «En esta página» o «Índice del artículo». */
    ariaLabel: string;
    /**
     * Fondo de la caja. Los tableros emparejan los dos árboles: la funcionalidad va en
     * surface-suave en escritorio y en móvil; el artículo, en blanco en los dos.
     */
    fondoMovil: 'suave' | 'blanco';
    /** Pie del índice de escritorio, bajo un divisor (el «Probar gratis →» de la funcionalidad). */
    pie?: ReactNode;
}

/** La cabecera fija mide 88 px; con 24 de aire, nada por encima de los 112 px se está leyendo. */
const ALTO_CABECERA_FIJA = 112;
/** La zona de activación va de la cabecera fija hasta el 45 % del alto de la ventana. */
const PROPORCION_FIN_ZONA = 0.45;

function finDeLaZona(): number {
    return Math.max(ALTO_CABECERA_FIJA, window.innerHeight * PROPORCION_FIN_ZONA);
}

function topDe(id: string): number | null {
    const nodo = document.getElementById(id);
    return nodo ? nodo.getBoundingClientRect().top : null;
}

/** Al final de la página la última sección puede no llegar nunca a la línea: entonces manda ella. */
function alFinalDeLaPagina(): boolean {
    const alto = document.documentElement.scrollHeight;
    return window.scrollY > 0 && Math.ceil(window.scrollY + window.innerHeight) >= alto - 2;
}

/**
 * La sección actual se calcula por posición en cada scroll, no por los avisos de un observador de
 * intersección: el primer título dentro de la zona de activación; si no hay ninguno, la última
 * sección cuyo título ya quedó bajo la cabecera; y si tampoco, la primera. El observador solo avisa
 * de cambios, así que un salto (tecla Fin, arrastrar la barra, recargar a mitad de página) que
 * dejaba el último título por encima de la zona no avisaba de nada y el índice se quedaba en la
 * primera entrada (UAT en Chrome, 17-22).
 */
function seccionActualDe(orden: string[]): string {
    if (alFinalDeLaPagina()) return orden[orden.length - 1];
    const tops = orden.map((id) => ({ id, top: topDe(id) })).filter((s): s is { id: string; top: number } => s.top !== null);
    const enZona = tops.find((s) => s.top >= ALTO_CABECERA_FIJA && s.top <= finDeLaZona());
    if (enZona) return enZona.id;
    const pasadas = tops.filter((s) => s.top < ALTO_CABECERA_FIJA);
    return pasadas[pasadas.length - 1]?.id ?? orden[0];
}

function vigilarSecciones(orden: string[], alCambiar: (id: string) => void): () => void {
    const recalcular = () => alCambiar(seccionActualDe(orden));
    recalcular();
    window.addEventListener('scroll', recalcular, { passive: true });
    window.addEventListener('resize', recalcular);
    return () => {
        window.removeEventListener('scroll', recalcular);
        window.removeEventListener('resize', recalcular);
    };
}

/** `ids` va unido por «|» para que un array nuevo con los mismos ids no reinicie la vigilancia. */
function useSeccionActual(ids: string, activo: boolean): [string, (id: string) => void] {
    const [actual, setActual] = useState(ids.split('|')[0]);
    useEffect(() => {
        if (!activo) return;
        return vigilarSecciones(ids.split('|'), setActual);
    }, [ids, activo]);
    return [actual, setActual];
}

/**
 * Cada fondo trae la densidad de su tablero: la guía de funcionalidad (suave) separa los
 * enlaces 4 px con 36 de alto; el índice del artículo (blanco), 2 px con 34.
 */
const ESTILOS_CAJA = {
    suave: { fondo: 'bg-surface-suave', hueco: 'gap-ds-1', alto: 'min-h-9' },
    blanco: { fondo: 'bg-surface-2', hueco: 'gap-[2px]', alto: 'min-h-[34px]' },
} as const;

type EstiloCaja = (typeof ESTILOS_CAJA)[keyof typeof ESTILOS_CAJA];

interface PropiedadesEscritorio {
    entradas: EntradaIndice[];
    titulo: string;
    ariaLabel: string;
    estilo: EstiloCaja;
    pie?: ReactNode;
    actual: string;
    alElegir: (id: string) => void;
}

function IndiceEscritorio({ entradas, titulo, ariaLabel, estilo, pie, actual, alElegir }: PropiedadesEscritorio) {
    return (
        <nav
            aria-label={ariaLabel}
            className={`hidden flex-col rounded-ds-lg border-2 border-ink p-ds-4 lg:sticky lg:top-28 lg:flex ${estilo.hueco} ${estilo.fondo}`}
        >
            <span className="mb-[6px] text-[12px] font-bold uppercase leading-[1.45] tracking-[0.1em] text-on-surface-variant">{titulo}</span>
            {entradas.map((e) => (
                <a
                    key={e.id}
                    href={`#${e.id}`}
                    onClick={() => alElegir(e.id)}
                    aria-current={e.id === actual ? 'location' : undefined}
                    className={`flex ${estilo.alto} items-center rounded-ds-sm px-[10px] py-ds-1 text-ds-200 leading-[1.35] text-ink no-underline hover:underline ${e.id === actual ? 'bg-tint-accent font-bold' : 'font-normal'}`}
                >
                    {e.texto}
                </a>
            ))}
            {pie && (
                <>
                    <span aria-hidden="true" className="my-ds-2 h-px bg-surface-3" />
                    <div className="text-ds-200 font-bold text-primary">{pie}</div>
                </>
            )}
        </nav>
    );
}

interface PropiedadesMovil {
    entradas: EntradaIndice[];
    titulo: string;
    ariaLabel: string;
    fondo: string;
}

// También es un <nav>, para que en móvil el índice sea un punto de referencia como en escritorio. Solo
// se ve uno de los dos, así que el nombre repetido no confunde (IN-13).
function IndiceMovil({ entradas, titulo, ariaLabel, fondo }: PropiedadesMovil) {
    const [abierto, setAbierto] = useState(false);
    const refBoton = useRef<HTMLButtonElement>(null);
    const idLista = useId();

    const alTeclear = (evento: KeyboardEvent<HTMLElement>) => {
        if (evento.key !== 'Escape' || !abierto) return;
        setAbierto(false);
        refBoton.current?.focus();
    };

    return (
        <nav aria-label={ariaLabel} onKeyDown={alTeclear} className={`overflow-hidden rounded-[14px] border-2 border-ink lg:hidden ${fondo}`}>
            <button
                ref={refBoton}
                type="button"
                aria-expanded={abierto}
                aria-controls={idLista}
                onClick={() => setAbierto((valor) => !valor)}
                className="flex min-h-[52px] w-full items-center justify-between bg-transparent px-[14px] font-sans text-[15px] font-bold text-ink"
            >
                {titulo}
                <span aria-hidden="true">{abierto ? '−' : '+'}</span>
            </button>
            <div id={idLista} hidden={!abierto} className="px-ds-2 pb-[10px]">
                {entradas.map((e) => (
                    <a
                        key={e.id}
                        href={`#${e.id}`}
                        onClick={() => setAbierto(false)}
                        className="flex min-h-11 items-center px-ds-2 text-[15px] text-ink no-underline"
                    >
                        {e.texto}
                    </a>
                ))}
            </div>
        </nav>
    );
}

/**
 * El índice de la guía de funcionalidad y del artículo del blog (UI-SPEC §Interaction 7).
 * Escritorio: columna fija con la sección en vista marcada por `aria-current="location"`.
 * Móvil: desplegable cerrado por defecto que se cierra al elegir y con Escape. Con menos de
 * dos secciones un índice no orienta nada, así que no se pinta.
 */
export default function IndicePagina({ entradas, etiqueta, ariaLabel, fondoMovil, pie }: PropiedadesIndicePagina) {
    // Solo el índice de escritorio necesita el scroll-spy: en móvil (árbol oculto) no se observa nada.
    const esEscritorio = useEsEscritorio();
    const [actual, setActual] = useSeccionActual(entradas.map((e) => e.id).join('|'), esEscritorio);
    if (entradas.length < 2) return null;

    const estilo = ESTILOS_CAJA[fondoMovil];
    return (
        <>
            <IndiceEscritorio
                entradas={entradas}
                titulo={etiqueta}
                ariaLabel={ariaLabel}
                estilo={estilo}
                pie={pie}
                actual={actual}
                alElegir={setActual}
            />
            <IndiceMovil entradas={entradas} titulo={etiqueta} ariaLabel={ariaLabel} fondo={estilo.fondo} />
        </>
    );
}
